<script lang="ts">
  import { onMount } from 'svelte';
  import { editorStore, currentData } from '../../stores/editorStore';
  import { undoStore } from '../../stores/undoStore';
  import { announceToScreenReader } from '../../lib/accessibility';
  import { validateFramework, getSlotTypeSummary } from '@blather/shared';
  import type { FrameworkFile, Framework, Category } from '@blather/shared';

  let overview: any = null;
  let selectedCategory: Category | null = null;
  let newFramework: Partial<Framework> = {
    id: '',
    marker: '',
    template: '',
  };
  let availableSlotTypes: string[] = [];

  onMount(async () => {
    try {
      overview = await editorStore.loadOverview();
      await editorStore.setMode('frameworks', overview);
      availableSlotTypes = overview.wordbanks;
    } catch (error) {
      console.error('Failed to load overview:', error);
    }
  });

  $: frameworkFile = $currentData as FrameworkFile | null;
  $: frameworks = frameworkFile?.frameworks || [];
  $: validationErrors = selectedCategory && newFramework.template
    ? validateFramework(newFramework as Framework, selectedCategory, availableSlotTypes)
    : [];
  $: hasErrors = validationErrors.some(e => e.severity === 'error');
  $: slotSummary = newFramework.template ? getSlotTypeSummary(newFramework.template) : '';

  async function selectCategory(category: Category) {
    selectedCategory = category;
    try {
      await editorStore.selectItem(category);
      announceToScreenReader(`Loaded ${category} frameworks with ${frameworks.length} items`, 'polite');
    } catch (error) {
      console.error('Failed to load frameworks:', error);
    }
  }

  function addFramework() {
    if (!selectedCategory || !frameworkFile || hasErrors) return;

    const framework: Framework = {
      id: newFramework.id!.trim(),
      marker: newFramework.marker as any || '',
      template: newFramework.template!.trim(),
    };

    // Check for duplicate ID
    if (frameworks.some(f => f.id === framework.id)) {
      announceToScreenReader(`Framework ID "${framework.id}" already exists`, 'assertive');
      return;
    }

    const currentFile = frameworkFile;
    const updatedFile: FrameworkFile = {
      ...currentFile,
      frameworks: [...currentFile.frameworks, framework],
    };

    undoStore.recordAction({
      type: 'add',
      target: 'framework',
      targetKey: selectedCategory,
      itemKey: framework.id,
      previousValue: currentFile,
      newValue: updatedFile,
    });

    editorStore.updateLocalData('frameworks', selectedCategory, updatedFile);

    // Reset form
    newFramework = { id: '', marker: '', template: '' };
    announceToScreenReader(`Added framework: ${framework.id}`, 'polite');
  }

  function deleteFramework(index: number) {
    if (!selectedCategory || !frameworkFile) return;

    const frameworkToDelete = frameworks[index];
    const currentFile = frameworkFile;
    const updatedFile: FrameworkFile = {
      ...currentFile,
      frameworks: currentFile.frameworks.filter((_, i) => i !== index),
    };

    undoStore.recordAction({
      type: 'delete',
      target: 'framework',
      targetKey: selectedCategory,
      itemKey: frameworkToDelete.id,
      previousValue: currentFile,
      newValue: updatedFile,
    });

    editorStore.updateLocalData('frameworks', selectedCategory, updatedFile);
    announceToScreenReader(`Deleted framework: ${frameworkToDelete.id}`, 'polite');
  }
</script>

<div class="editor-container">
  <section aria-labelledby="category-select-heading">
    <h2 id="category-select-heading">Select Category</h2>

    {#if $editorStore.isLoading && !overview}
      <p>Loading categories...</p>
    {:else if overview}
      <select
        bind:value={selectedCategory}
        on:change={() => selectedCategory && selectCategory(selectedCategory)}
        aria-label="Choose category to edit"
        class="category-select"
      >
        <option value={null}>-- Select a category --</option>
        {#each overview.frameworkCategories as cat}
          <option value={cat}>{cat}</option>
        {/each}
      </select>
    {:else}
      <p class="error-text">Failed to load categories</p>
    {/if}
  </section>

  {#if selectedCategory && frameworkFile}
    <section aria-labelledby="frameworks-heading">
      <h2 id="frameworks-heading">Frameworks ({frameworks.length})</h2>

      {#if frameworks.length === 0}
        <p class="empty-message">No frameworks in this category. Add one below!</p>
      {:else}
        <ul class="framework-list" role="list">
          {#each frameworks as framework, i}
            <li class="framework-item">
              <div class="framework-header">
                <strong>{framework.id}</strong>
                {#if framework.marker}
                  <span class="marker-badge">{framework.marker}</span>
                {/if}
              </div>
              <div class="framework-template">{framework.template}</div>
              <button
                on:click={() => deleteFramework(i)}
                aria-label="Delete framework: {framework.id}"
                class="delete-button"
              >
                Delete
              </button>
            </li>
          {/each}
        </ul>
      {/if}

      <div class="add-framework-section">
        <h3>Add New Framework</h3>

        <div class="form-group">
          <label for="fw-id">ID <span class="required">*</span></label>
          <input
            id="fw-id"
            type="text"
            bind:value={newFramework.id}
            placeholder="e.g., thing_opener_1"
            aria-required="true"
          />
          <p class="hint">Should start with "{selectedCategory}_" (lowercase, alphanumeric, underscores only)</p>
        </div>

        <div class="form-group">
          <label for="fw-marker">Marker</label>
          <select id="fw-marker" bind:value={newFramework.marker}>
            <option value="">Random (used in presentation)</option>
            <option value="opener">Opener (opening clue phase)</option>
            <option value="first">First (first clue in presentation)</option>
          </select>
        </div>

        <div class="form-group">
          <label for="fw-template">Template <span class="required">*</span></label>
          <textarea
            id="fw-template"
            bind:value={newFramework.template}
            placeholder="e.g., This is [determiner] [adjective] [noun]"
            rows="3"
            aria-required="true"
            aria-describedby="template-hint slot-summary"
          />
          <p id="template-hint" class="hint">Use [slotType] for placeholders. Available slot types: {availableSlotTypes.join(', ')}</p>
          {#if slotSummary}
            <p id="slot-summary" class="slot-summary">{slotSummary}</p>
          {/if}
        </div>

        {#if validationErrors.length > 0}
          <div class="validation-errors" role="alert" aria-live="polite">
            <h4>Validation {hasErrors ? 'Errors' : 'Warnings'}:</h4>
            <ul>
              {#each validationErrors as error}
                <li class="validation-error {error.severity}">
                  <strong>{error.field}:</strong> {error.message}
                </li>
              {/each}
            </ul>
          </div>
        {/if}

        <button
          on:click={addFramework}
          disabled={!newFramework.id || !newFramework.template || hasErrors}
          aria-disabled={!newFramework.id || !newFramework.template || hasErrors}
          class="add-button"
        >
          Add Framework
        </button>
      </div>
    </section>
  {/if}
</div>

<style>
  .editor-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem;
  }

  section {
    margin-bottom: 2rem;
  }

  h2 {
    margin-top: 0;
    margin-bottom: 1rem;
    color: var(--color-text-primary, #333);
  }

  h3 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 1.125rem;
    color: var(--color-text-primary, #333);
  }

  h4 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  .category-select {
    width: 100%;
    max-width: 400px;
    padding: 0.75rem;
    font-size: 1rem;
    border: 2px solid var(--color-border, #ddd);
    border-radius: 4px;
    background: white;
  }

  .category-select:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .framework-list {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
  }

  .framework-item {
    padding: 1rem;
    background: white;
    border: 1px solid var(--color-border, #ddd);
    margin-bottom: 0.75rem;
    border-radius: 4px;
    position: relative;
  }

  .framework-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .marker-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    background: var(--color-primary, #007bff);
    color: white;
    font-size: 0.75rem;
    border-radius: 4px;
    font-weight: 500;
  }

  .framework-template {
    font-family: monospace;
    padding: 0.5rem;
    background: var(--color-bg-secondary, #f8f9fa);
    border-radius: 4px;
    margin-bottom: 0.75rem;
    color: var(--color-text-secondary, #666);
  }

  .delete-button {
    background: var(--color-danger, #dc3545);
    color: white;
    border: none;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .delete-button:hover {
    background: var(--color-danger-hover, #c82333);
  }

  .delete-button:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .add-framework-section {
    background: var(--color-bg-secondary, #f8f9fa);
    padding: 1.5rem;
    border-radius: 8px;
    border: 2px dashed var(--color-border, #ddd);
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--color-text-primary, #333);
  }

  .required {
    color: var(--color-danger, #dc3545);
  }

  input[type="text"],
  select,
  textarea {
    width: 100%;
    padding: 0.75rem;
    font-size: 1rem;
    border: 2px solid var(--color-border, #ddd);
    border-radius: 4px;
    font-family: inherit;
  }

  textarea {
    font-family: monospace;
    resize: vertical;
  }

  input:focus,
  select:focus,
  textarea:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .hint {
    margin: 0.5rem 0 0 0;
    font-size: 0.875rem;
    color: var(--color-text-secondary, #666);
  }

  .slot-summary {
    margin: 0.5rem 0 0 0;
    font-size: 0.875rem;
    color: var(--color-primary, #007bff);
    font-weight: 500;
  }

  .validation-errors {
    margin-bottom: 1rem;
    padding: 1rem;
    background: var(--color-error-bg, #f8d7da);
    border: 1px solid var(--color-error-border, #f5c6cb);
    border-radius: 4px;
  }

  .validation-errors ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  .validation-error {
    margin-bottom: 0.5rem;
    color: var(--color-error-text, #721c24);
  }

  .validation-error.warning {
    color: var(--color-warning-text, #856404);
  }

  .validation-error:last-child {
    margin-bottom: 0;
  }

  .add-button {
    background: var(--color-success, #28a745);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 4px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .add-button:hover:not(:disabled) {
    background: var(--color-success-hover, #218838);
  }

  .add-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .add-button:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .empty-message {
    padding: 2rem;
    text-align: center;
    color: var(--color-text-secondary, #666);
    background: var(--color-bg-secondary, #f8f9fa);
    border-radius: 8px;
  }

  .error-text {
    color: var(--color-danger, #dc3545);
  }
</style>
